import type { BrandingData } from './user';

export interface SubscriptionAddOnStatus {
  key: string;
  /**
   * Stripe subscription-item id. Absent until the add-on has actually been bought or
   * trialled, which is what makes `!id` mean "never acquired" (buildAddonList reads the
   * same field off these rows for `canStartTrial`).
   */
  id?: string;
  isTrial?: boolean;
  isActive?: boolean;
  trialEndDate?: string;
  version?: string;
  maxQuantity?: number;
  /** Tier within a multi-tier add-on (e.g., 'standard' | 'pro' for backgroundmusic). */
  planFeature?: string;
  /** OptiDev only — quantity stays in sync with the account screen count when true. */
  enableAllScreens?: boolean;
}

/**
 * Dunning — the graduated past-due ladder. Read from `me.payment.dunning`, which
 * the backend re-reads fresh from Mongo on every `me` call (the rest of `payment`
 * comes from a 15-minute cache), so this is never stale after a payment lands.
 *
 * Absent / null means the account has never been in dunning, or has been cured.
 *
 * Backend source of truth: `optisigns-open-api/src/app/dunning/dunning-ladders.seed.ts`
 */
export const DUNNING_STATES = {
  NONE: 'none',
  /** Notices sent. The account is FULLY functional. */
  NOTIFIED: 'notified',
  /** No new screens, users or publishing. Existing screens KEEP PLAYING. */
  LIMITED: 'limited',
  /** Playback paused. Nothing is deleted; it resumes when payment clears. */
  SUSPENDED: 'suspended',
  /** Held for a manager decision. Never auto-advances to suspended. */
  REVIEW: 'review',
} as const;
export type DunningState = (typeof DUNNING_STATES)[keyof typeof DUNNING_STATES];

/** Which ladder the episode is walking — card-on-file vs PO / net terms. */
export const DUNNING_LADDERS = { CC: 'cc', PO: 'po' } as const;
export type DunningLadder = (typeof DUNNING_LADDERS)[keyof typeof DUNNING_LADDERS];

/** A recorded acknowledgement of a dunning notice ("click recorded"). */
/**
 * Dismissal tri-state for a pre-emptive advisory, mirroring `payment.trialBanner`.
 * 0 = show · 1 = dismissed (returns near the date) · 2 = permanently dismissed.
 */
export const ADVISORY_DISMISS = { SHOW: 0, DISMISSED: 1, PERMANENT: 2 } as const;
export type AdvisoryDismissState = (typeof ADVISORY_DISMISS)[keyof typeof ADVISORY_DISMISS];

/**
 * A pre-emptive billing advisory — the CC ladder's day −30 (card expiring) and
 * day −7 (upcoming charge) rungs, and the PO ladder's day −7 (upcoming invoice).
 *
 * Read from `me.payment.dunningAdvisory`. Deliberately SEPARATE from
 * `CompanyDunning`: nothing is past due, nothing is enforceable, and it must never
 * be confused with a real dunning episode.
 */
export interface CompanyDunningAdvisory {
  /**
   * `annualRenewalCharge` | `annualRenewalInvoice` | `cardExpiry` |
   * `upcomingCharge` | `upcomingInvoice` — the ids staff-app's ladder raises.
   */
  step: string;
  ladder: DunningLadder;
  /** The date being warned about — card expiry, or the next charge / due date. */
  advisoryAt?: string | null;
  /** Last 4 of the card, so the copy can say which one. */
  cardLast4?: string | null;
  amountDueCents?: number | null;
  currency?: string | null;
  dismissState?: AdvisoryDismissState | number | null;
  /** Days before `advisoryAt` at which a once-dismissed advisory returns. */
  reshowWithinDays?: number | null;
  // The ack trail and bookkeeping dates are stored but not sent to the browser —
  // open-api's `me` returns an allow-list (dunning-public.ts).
}

export interface CompanyDunning {
  /**
   * What the portal renders — and ONLY that.
   *
   * The episode itself (state, rungs, schedule, the invoice being chased, the branch)
   * lives in staff-app's Postgres. What arrives here is a projection the backend
   * computes: four decisions it has already made, so the browser renders rather than
   * re-derives. Every field the old shape carried for the frontend to reason with —
   * `state`, `ladder`, `step`, `startedAt`, `branch`, `hold` — is deliberately gone.
   */

  /** Show the persistent past-due banner. Resolved from the current rung's channels. */
  showBanner?: boolean | null;
  /**
   * Show the login popup. Already accounts for a hold: a held episode (customer
   * replied, dispute) keeps its banner but never nags, and the backend says so here
   * rather than exporting the hold for the browser to interpret.
   */
  showPopup?: boolean | null;
  /**
   * New screens, users and publishing are blocked. Covers the ladder's `limited` and
   * `review` states; a suspended account is `payment.isActive: false` and never
   * reaches the portal at all, so there is no third case to render.
   */
  isLimited?: boolean | null;
  /**
   * The date the customer is told about — "names the shutoff date" in both diagrams.
   * Computed and stored server-side so the banner and the notice emails always quote
   * the same day, and so the frontend never reimplements ladder arithmetic.
   */
  shutoffAt?: string | null;
}

export interface CompanyPayment {
  planFeature: string;
  trialEndDate: string;
  isTrial: boolean;
  isActive: boolean;
  trialBanner: number;
  plan: string;
  nextCharge: string;
  billing_email: string;
  subscriptionIdAddOns?: SubscriptionAddOnStatus[];
  firstScreenAssignmentEventFired?: string;
  /** Timestamp when first device was paired, or 'not_triggered' */
  firstDevicePaired?: string | null;
  /** Timestamp when first content was created, or 'not_triggered' */
  firstContentCreated?: string;
  /** Timestamp when first playlist was created, or 'not_triggered' */
  firstPlaylistCreated?: string;
  /** Gate for the `trialCreated` analytics event — 'NOT_TRIGGERED' until
   *  the trial-creation GTM event has been fired once per account, then
   *  'TRIGGERED'. Mirrors scio1 `company.payment.trialStartEventFired`.
   *  Flipped by the `triggerTrialStartEvent` mutation. */
  trialStartEventFired?: string;
  /** Timestamp when first playlist had content (assets) saved, or 'not_triggered' */
  firstPlaylistContentAdded?: string;
  /** Stripe subscription item ID for the Yearly + Free Device tier (SCIO 1.0 parity). */
  subscription_freedevice_id?: string;
  /**
   * Graduated past-due ladder. Independent of `isActive` above — an account in
   * dunning keeps full portal access so it can still pay us.
   */
  dunning?: CompanyDunning | null;
  /**
   * Pre-emptive advisory (day −30 / −7). Independent of `dunning` — nothing is
   * past due and nothing is enforceable.
   */
  dunningAdvisory?: CompanyDunningAdvisory | null;
}

export interface CompanyLimitation {
  maxQuantity: number;
  quantity: number;
  maxStorage: number;
  usedStorage: number;
  disableDownload?: boolean;
  maxCustomFonts?: number;
  /** Screens on the Yearly + Free Device tier (counted separately from maxQuantity). */
  maxQuantityFreeDevice?: number;
  // OptiEdge MDM Phase 1 — Room Signage license allocation (per-vendor reserved counts).
  // Allocating reserves licenses out of `quantity` (same pool as Screens).
  googleRoomsMaxQuantity?: number;
  zoomRoomsMaxQuantity?: number;
  teamsRoomsMaxQuantity?: number;
  webexRoomsMaxQuantity?: number;
  /** Denormalised sum of the four *RoomsMaxQuantity fields; cap math reads this single field. */
  roomsQuantity?: number;
  // Free demo/test licenses on top of `roomsQuantity`. Default 3, or
  // ceil(0.05 * maxQuantity) when maxQuantity > 60. Recomputed lazily on read.
  roomsGraceLicense?: number;
}

export interface CompanyFeatures {
  // Core plan features
  allowTeams: boolean;
  samlSSO: boolean;
  roleSecurity: boolean;
  folderLevelSecurity: boolean;
  advancedDeviceManagement: boolean;
  advancedSchedule: boolean;
  cecSchedule: boolean;
  /** Pro Plus+ PLAN flag — branded look + `*.optisigns.net` subHost + SAML account-id. */
  branding: boolean;
  /** Paid custom-domain ADD-ON flag — gates REAL custom domains only (CF-for-SaaS ~$0.10/mo). See spec 17. */
  brandingCustomDomain?: boolean;
  /** Granted ONLY by the xSocial add-on — no plan tier includes it. Gates the xSocial app
   *  (see ADDON_ONLY_FEATURE_FLAGS) and the SmallApp's own entitlement. */
  xSocial?: boolean;
  remoteControl: boolean;
  lockdownDevice: boolean;
  sensor: boolean;
  proofOfPlay: boolean;
  /** Plan gate for Broadcast — scheduled captures delivered via the Reporting Engine (Pro Plus / Engage / Enterprise). */
  broadcast: boolean;
  ai: boolean;
  apiKey: boolean;
  apiAccessToken: boolean;
  engage: boolean;
  approvalWorkflow: boolean;
  /** Plan/feature gate for Revue (OptiSigns digital advertising marketplace).
   *  When true, the company can provision a Revue workspace via /app/s/revue. */
  contentApproval?: boolean;
  securityAuditLogging: boolean;
  customBrandCanva: boolean;
  alertPolicy: boolean;
  remoteCommand: boolean;
  /** Plan gate for SecureLink (WireGuard VPN for engineer remote troubleshooting). */
  secureLink?: boolean;
  /** Hidden staff kill switch (L1 §5.1). true ⇒ SecureLink remote access DISABLED for this company
   *  (overrides the plan gate). Read-only on the client — customers can't set it. */
  disableSecureLink?: boolean;
  externalCom: boolean;
  utilsMDM?: boolean;
  /** Plan gate for OptiEdge MDM — unlocks Devices + Fleets pages. */
  mdm?: boolean;
  comLog: boolean;
  shareLink: boolean;
  totpMfa: boolean;
  phoneMfa: boolean;
  contentPlaylistTag: boolean;
  subPlaylistControl: boolean;
  enforcePasswordComplexity?: boolean;
  playEvery: boolean;
  resumeOnNextPlay: boolean;
  feedsCreate: boolean;
  feedsManage?: boolean;
  feedsCategories?: boolean;
  feeds?: boolean;
  enterpriseAPP?: boolean;
  isEnterprise?: boolean;
  /** When true, suppress the unpaid-invoice pre-check in the subscription wizard.
   *  SCIO 1.0 parity: subscriptionPlanModal.js:1956. */
  disablePaymentCheck?: boolean;
  // Apps & integrations
  allowPowerBI?: boolean;
  powerbiServicePrincipal?: boolean;
  allowLooker?: boolean;
  hostedWebsite?: boolean;
  webScripting?: boolean;
  scriptInjection?: boolean;
  interactiveKiosk?: boolean;
  securedDashboardApp?: boolean;
  liveTvAds?: boolean;
  dynamicDataMapping?: boolean;
  // Device management
  massProvision?: boolean;
  scim?: boolean;
  orderManagedDevice?: boolean;
  /** International ordering — when true, suppress the "Pre-configured devices
   *  ship US-only" warning dialog on Order Device. SCIO 1.0 parity:
   *  orderDeviceComponent.js:1112,1130 — `notAllowInt = !feature.allowIntlOrder`. */
  allowIntlOrder?: boolean;
  staticEgress?: boolean;
  optiSignsScreenCapture?: boolean;
  virtualScreenCountLimit?: number;
  primaryZone?: boolean;
  // Designer & content
  lockableObjectDesigner?: boolean;
  designerOpenAIText?: boolean;
  openAIApiKey?: boolean;
  enforceBranding?: string;
  displayFormat?: string;
  uploadFont?: boolean;
  disableLimitUploadResolutionImage?: boolean;
  // AI features
  enableContentAIDetection?: boolean;
  disableAIBrandPortal?: boolean;
  // API & gateway
  apiGateway?: boolean;
  apiUploadFiles?: boolean;
  // Billing & admin
  teamBilling?: boolean;
  // Emergency & alerts
  CAPAlert?: boolean;
  emergencyFeed?: boolean;
  // Proof of play
  enableHourlyPOPExtract?: boolean;
  enablePOPExtractPriority?: boolean;
  // Preferences page fields
  wifiFallback?: boolean;
  enforceSSO?: string;
  disableVideoConversion?: boolean;
  mcpConnector?: boolean;
  // Infrastructure / Data Residency
  bucket?: string;
  disableS3?: boolean;
  disableDOSpaces?: boolean;
  aericast?: boolean;
  optidev?: boolean;
  // SmallApp manifest playground
  smallappManifest?: boolean;
  // Free-device plan gating (SCIO 1.0 subscriptionPlanModal.js isDisablePlan)
  enableYearlyFreeDevice?: boolean;
  allowAddYearlyFreeDevice?: boolean;
  allowAddYearly10Plan?: boolean;
  allowAddMonthlyPlan?: boolean;
}

export interface CompanyFeatureData {
  language: string;
  defaultPLDuration: number;
  defaultPLTransition: string;
  isSecurityAuditLogging: boolean;
  dateFormat: string;
  timeFormat: string;
  numberFormat: string;
  scaleTemperature: string;
  windSpeedUnit: string;
  defaultValueType: string;
  enableSubAccount: boolean;
  /** Account-wide cap on Emergency Feed assets (default 50). scio1 parity:
   *  emergencyFeedSchema.js server hook counts {appType, accountId}. */
  emergencyFeedLimit?: number;
  /** Per-account cap on Hosted Website assets (team-scoped). No built-in
   *  default — unlimited unless set. Read by resolveCreationLimit. */
  hostedWebsiteLimitation?: number;
  /** Per-account overrides for the Free Plan playlist caps (items, then bytes), defaulting to
   *  30 and 200 MB. open-api's `validatePlaylistAssignAsset` reads them straight off the Mongo
   *  document and does NOT declare them on the `FeatureData` GraphQL type, so no query can
   *  select them today and they arrive undefined — `resolveFreePlanPlaylistLimits` falls back
   *  to the defaults. Declared here so that adding the two `@Field`s server-side is all it
   *  takes for the client to honour an override. Nothing in any repo writes them; they exist
   *  as a manual escape hatch. */
  nFreePlanItemsOnPlaylist?: number;
  nFreePlanFileSizePlaylist?: number;
  // Playlist defaults
  defaultPLScaleImage?: string;
  defaultPLScaleVideo?: string;
  defaultPLScaleDocument?: string;
  targetTagsDefault?: string;
  // Kiosk/Engage nav defaults
  defaultNavIconPosition?: string;
  defaultNavIconStyle?: string;
  defaultNavIconColorType?: string;
  defaultNavIconColorCode?: string;
  defaultNavIconSizeType?: string;
  defaultNavIconSize?: number;
  defaultNavBackgroundColor?: string;
  defaultNavOpacity?: number;
  // Security/admin preferences
  auditLogsAllowIp?: boolean;
  isRemoteCommand?: boolean;
  /** Account-level toggle for SecureLink (WireGuard VPN). Gated by feature.secureLink. */
  secureLink?: boolean;
  enableWifiFallback?: boolean;
  wifiFallbackRetryInterval?: number;
  wifiFallbackMaxRetryDay?: number;
  // COM log analytics settings
  comLogStatus?: boolean;
  comLogRealTime?: boolean;
  comLogInterval?: number;
  comLogType?: string;
  comLogWeek?: string;
  comLogMonth?: number;
  comLogRunWindows?: number;
  comLogTimezone?: string;
  comLogEmail?: string;
  comLogEmailFrom?: string;
  comLogAWSS3?: boolean;
  comLogAWSS3Bucket?: string;
  comLogAWSS3BucketPath?: string;
  comLogAWSS3Region?: string;
  // Proof of Play settings
  proofOfPlayStatus?: boolean;
  proofOfPlayRealTime?: boolean;
  proofOfPlayInterval?: number;
  proofOfPlayType?: string;
  proofOfPlayWeek?: number;
  proofOfPlayMonth?: number;
  proofOfPlayRunWindows?: number;
  proofOfPlayTimezone?: string;
  proofOfPlayEmail?: string;
  proofOfPlayEmailFrom?: string;
  proofOfPlayAWSS3?: boolean;
  proofOfPlayAWSS3Bucket?: string;
  proofOfPlayAWSS3BucketPath?: string;
  proofOfPlayAWSS3Region?: string;
  proofOfPlayAWSS3Endpoint?: string;
  proofOfPlayAWSS3Provider?: string;
}

export interface CompanyContact {
  name?: string;
  phoneNumber?: string;
  email?: string;
}

export interface CompanyLocation {
  address1?: string;
  address2?: string;
  city?: string;
  zip?: string;
  state?: string;
  country?: string;
}

export interface CompanyPartner {
  name?: string;
  locked?: boolean;
}

export interface CompanyGetStarted {
  triggered: boolean;
  completed: boolean;
  /** ISO timestamp when get-started was marked as completed */
  completedDate?: string | null;
}

export interface CompanyOptiDev {
  workspaceId?: string;
}

export interface CompanyInfo {
  _id: string;
  name: string;
  industry: string;
  phoneNumber?: string;
  contact?: CompanyContact;
  location?: CompanyLocation;
  partner?: CompanyPartner;
  payment: CompanyPayment;
  limitation: CompanyLimitation;
  feature: CompanyFeatures;
  featureData: CompanyFeatureData;
  brandingData: BrandingData;
  getStarted: CompanyGetStarted;
  /** A/B experiment assignments keyed by experiment flag (e.g. `{ 'add-screen-cta-v1': 'treatment' }`).
   *  Written once per experiment on the backend (first-writer-wins). */
  experiments?: Record<string, string>;
  optidev?: CompanyOptiDev;
  /** Linked OptiSound shadow account id (set by ensureOptisoundAccount). Used by the
   *  embedded OptiSound portal to authenticate as this company's shadow account. */
  optisoundAccountId?: string;
  createdAt: string;
  noOfScreens: number;
  scioVersion?: number;
  hasDoneOnboardingSurvey?: boolean;
  /**
   * Set when this account inherited branding from another account (child host).
   * Used by Branding Settings to surface parent-account info and compose
   * child-host login URLs.
   *
   * Also set when this account was created as a sub-account via Sub-Account
   * Billing (addNewSubAccount). Used by SubAccountBillingPage + SettingsLayout
   * to hide the Sub-Account Billing tab for child accounts (#1272).
   */
  parentAccountId?: string;

  /**
   * Set when this account was linked as a sub-account via the Link Existing
   * Account flow (linkSubAccount). Same downstream uses as parentAccountId —
   * both indicate "this account is a child of another billing account".
   */
  linkedAccountId?: string;
}
